import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import { Facebook, Instagram } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import Inbox from './Inbox'

function UserProfile(props) {
  const { response, handleLogout, handleDeleteData } = props
  const [isReportDialogOpen, setReportDialogOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [problemDescription, setProblemDescription] = useState('')
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [skipReplied, setSkipReplied] = useState(true)
  const [updatingSkipReplied, setUpdatingSkipReplied] = useState(false)
  const [skipDialogOpen, setSkipDialogOpen] = useState(false)
  const [pageContentOpen, setPageContentOpen] = useState(false)
  const [pageContentLoading, setPageContentLoading] = useState(false)
  const [pageContentError, setPageContentError] = useState(null)
  const [pageContentData, setPageContentData] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteStatus, setDeleteStatus] = useState('idle')
  const [deleteError, setDeleteError] = useState(null)

  const defaultBackendUrl =
    typeof window !== 'undefined' ? window.location.origin : ''
  const backendUrl = process.env.REACT_APP_BACKEND_URL || defaultBackendUrl
  const userAvatarUrl =
    response?.profile_picture_url ||
    response?.ig_accounts?.find((ig) => ig.profile_picture_url)
      ?.profile_picture_url ||
    null

  useEffect(() => {
    if (typeof response?.skip_replied_to_conversations === 'boolean') {
      setSkipReplied(response.skip_replied_to_conversations)
    }
  }, [response?.skip_replied_to_conversations])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const igLoginStatus = params.get('ig_login')
    if (!igLoginStatus) return

    if (igLoginStatus === 'success' && !selectedAsset) {
      const igAccounts = Array.isArray(response?.ig_accounts)
        ? response.ig_accounts
        : []
      const preferredIg =
        igAccounts.find(
          (ig) =>
            ig?.has_messaging_access &&
            (ig?.connection_source === 'instagram_login' ||
              ig?.connection_source === 'both'),
        ) || igAccounts.find((ig) => ig?.has_messaging_access)

      if (preferredIg) {
        const isIgLoginPreferred =
          preferredIg.connection_source === 'instagram_login' ||
          preferredIg.connection_source === 'both'
        const attachedPageName = response?.pages?.find(
          (page) => page.page_id === preferredIg.fb_page_id,
        )?.page_name

        setSelectedAsset({
          id: isIgLoginPreferred
            ? preferredIg.ig_id
            : preferredIg.fb_page_id || preferredIg.ig_id,
          label: preferredIg.ig_username,
          type: 'instagram',
          attachedPage: attachedPageName || 'Instagram Login',
          connectionSource: preferredIg.connection_source || 'facebook_login',
        })
      }
    }

    params.delete('ig_login')
    const nextQuery = params.toString()
    const cleanedUrl = `${window.location.pathname}${
      nextQuery ? `?${nextQuery}` : ''
    }${window.location.hash || ''}`
    window.history.replaceState({}, '', cleanedUrl)
  }, [response, selectedAsset])

  const renderPageAccessLabel = (page) =>
    page.discovered_via === 'business'
      ? 'Facebook • business access'
      : 'Facebook • profile access'

  const handleToggleSkipReplied = async (event) => {
    const nextValue = event.target.checked
    setSkipReplied(nextValue)
    setUpdatingSkipReplied(true)
    try {
      await axios.patch(
        `${backendUrl}/me/skip-replied`,
        { skip_replied_to_conversations: nextValue },
        { withCredentials: true },
      )
    } catch (error) {
      setSkipReplied(!nextValue)
    } finally {
      setUpdatingSkipReplied(false)
    }
  }

  const handleViewPageContent = async (page) => {
    setPageContentOpen(true)
    setPageContentLoading(true)
    setPageContentError(null)
    setPageContentData(null)
    try {
      const response = await axios.get(`${backendUrl}/page-content`, {
        withCredentials: true,
        params: { page_id: page.page_id, limit: 3 },
      })
      setPageContentData(response.data)
    } catch (error) {
      setPageContentError('Failed to load Page content.')
    } finally {
      setPageContentLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    setDeleteStatus('deleting')
    setDeleteError(null)
    const ok = await handleDeleteData({ delayMs: 2000 })
    if (ok) {
      setDeleteStatus('done')
    } else {
      setDeleteStatus('error')
      setDeleteError('Failed to delete data. Please try again.')
    }
  }

  if (selectedAsset) {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid item lg={10} md={12} xs={12}>
          <Inbox
            selectedAsset={selectedAsset}
            onBack={() => setSelectedAsset(null)}
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{ minHeight: '100vh', paddingBottom: '40px' }}
    >
      <Grid item lg={5} md={6} xs={12}>
        <Card elevation={3}>
          <CardHeader
            title="User Information"
            titleTypographyProps={{ variant: 'h5' }}
            avatar={
              <Avatar
                src={userAvatarUrl}
                alt={response?.name}
                variant="circle"
              />
            }
          />
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Hey {response?.name}, You are now logged in! No further action is
              required unless you want to view or reply to messages.
            </Typography>

            {(response?.provider !== 'instagram_login' ||
              (Array.isArray(response?.pages) && response.pages.length > 0)) && (
              <>
                <Typography variant="h6" gutterBottom>
                  Connected Facebook Pages:
                </Typography>
                <List>
                  {Array.isArray(response?.pages) && response.pages.length > 0 ? (
                    response.pages.map((page, index) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <Avatar
                            src={page.profile_picture_url}
                            alt={page.page_name}
                          >
                            {!page.profile_picture_url && <Facebook />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${page.page_name} (${renderPageAccessLabel(page)})`}
                          secondary={
                            !page.is_accessible
                              ? `⚠️ Page not usable: ${page.access_error || 'Authorization not granted for this Page'}${
                                  page.discovered_via === 'business' && page.business_name
                                    ? ` (Found via ${page.business_name})`
                                    : ''
                                }`
                              : page.has_messaging_access
                                ? ''
                                : '⚠️ This page does not have messaging access'
                          }
                          primaryTypographyProps={{ color: 'textPrimary' }}
                          secondaryTypographyProps={{
                            color:
                              !page.is_accessible || !page.has_messaging_access
                                ? 'error'
                                : 'textSecondary',
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!page.is_accessible || !page.has_messaging_access}
                          onClick={() =>
                            setSelectedAsset({
                              id: page.page_id,
                              label: page.page_name,
                              type: 'facebook',
                              discoveredVia: page.discovered_via || 'direct',
                            })
                          }
                        >
                          Open Inbox
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleViewPageContent(page)}
                          style={{ marginLeft: '8px' }}
                          disabled={!page.is_accessible}
                        >
                          View Page Content
                        </Button>
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No connected Facebook pages found.
                    </Typography>
                  )}
                </List>
              </>
            )}

            <Typography variant="h6" gutterBottom>
              Connected Instagram Accounts:
            </Typography>
            {Array.isArray(response?.ig_accounts) &&
            response.ig_accounts.length > 0 ? (
              <List>
                {response.ig_accounts.map((ig, index) => {
                  const isIgLoginOnly = !ig.fb_page_id
                  const secondaryText = ig.has_messaging_access
                    ? isIgLoginOnly
                      ? 'Connected via Instagram Login.'
                      : ''
                    : '⚠️ This Instagram account does not have messaging access'
                  const attachedPageName = response.pages?.find(
                    (page) => page.page_id === ig.fb_page_id,
                  )?.page_name
                  const primaryLabel = isIgLoginOnly
                    ? `${ig.ig_username} (Instagram Login)`
                    : `${ig.ig_username} (${attachedPageName || 'Unknown page'})`
                  return (
                  <ListItem key={index}>
                    <ListItemAvatar>
                      <Avatar src={ig.profile_picture_url} alt={ig.ig_username}>
                        {!ig.profile_picture_url && <Instagram />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={primaryLabel}
                      secondary={secondaryText}
                      primaryTypographyProps={{ color: 'textPrimary' }}
                      secondaryTypographyProps={{
                        color: ig.has_messaging_access
                          ? 'textSecondary'
                          : 'error',
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!ig.has_messaging_access}
                      onClick={() =>
                        setSelectedAsset({
                          id: isIgLoginOnly ? ig.ig_id : ig.fb_page_id,
                          label: ig.ig_username,
                          type: 'instagram',
                          attachedPage: attachedPageName || 'Instagram Login',
                          connectionSource:
                            ig.connection_source || 'facebook_login',
                        })
                      }
                    >
                      Open Inbox
                    </Button>
                  </ListItem>
                  )
                })}
              </List>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary">
                  No connected Instagram accounts found.
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  If you selected an Instagram account during login but do not
                  see it here:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Ensure the Instagram account is linked to a Facebook Page.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Ensure that Page was selected during authorization.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Ensure messaging permissions were granted for the Page.
                </Typography>
              </>
            )}

            <Button
              variant="contained"
              color="secondary"
              onClick={handleLogout}
              startIcon={<Facebook />}
              fullWidth
              style={{ marginTop: '10px' }}
            >
              Logout (Service Remains Active)
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setDeleteStatus('idle')
                setDeleteError(null)
                setDeleteDialogOpen(true)
              }}
              fullWidth
              style={{ marginTop: '10px' }}
            >
              Delete all my data
            </Button>

            <Dialog
              open={deleteDialogOpen}
              onClose={() =>
                deleteStatus === 'deleting' ? null : setDeleteDialogOpen(false)
              }
            >
              <DialogTitle>Delete all data?</DialogTitle>
              <DialogContent>
                {deleteStatus === 'idle' && (
                  <Typography variant="body2" color="textSecondary">
                    This will permanently delete your Threat Matrix data and log
                    you out. This cannot be undone.
                  </Typography>
                )}
                {deleteStatus === 'deleting' && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <CircularProgress size={16} />
                    Deleting your data...
                  </Typography>
                )}
                {deleteStatus === 'done' && (
                  <Typography variant="body2" color="textSecondary">
                    Deleted. Redirecting…
                  </Typography>
                )}
                {deleteStatus === 'error' && (
                  <Typography variant="body2" color="error">
                    {deleteError}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                {deleteStatus === 'idle' || deleteStatus === 'error' ? (
                  <>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button color="error" onClick={handleConfirmDelete}>
                      Delete
                    </Button>
                  </>
                ) : null}
              </DialogActions>
            </Dialog>

            <Dialog
              open={pageContentOpen}
              onClose={() =>
                pageContentLoading ? null : setPageContentOpen(false)
              }
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Page Content</DialogTitle>
              <DialogContent>
                {pageContentLoading && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <CircularProgress size={16} />
                    Loading Page content...
                  </Typography>
                )}
                {pageContentError && (
                  <Typography variant="body2" color="error">
                    {pageContentError}
                  </Typography>
                )}
                {pageContentData && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      {pageContentData.page?.name}
                    </Typography>
                    {pageContentData.posts?.length > 0 ? (
                      pageContentData.posts.map((post) => (
                        <div key={post.id} style={{ marginBottom: '12px' }}>
                          <Typography variant="body2">
                            {post.message || post.story || 'Post'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {post.created_time}
                          </Typography>
                        </div>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No recent posts found.
                      </Typography>
                    )}
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPageContentOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>

            <p className="user-profile-text">
              Thank you for authorising Threat Matrix Direct Messaging Services.
            </p>
            <p className="user-profile-text">
              You may opt out of the service at any time using the delete button
              above.
            </p>

            <Divider style={{ marginTop: '16px', marginBottom: '12px' }} />
            <Typography variant="subtitle2" gutterBottom>
              Conversation retention
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Choose whether to ignore conversations which have been replied to.
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => setSkipDialogOpen(true)}
              disabled={updatingSkipReplied}
            >
              {skipReplied
                ? 'Ignore replied conversations (enabled)'
                : 'Ignore replied conversations (disabled)'}
            </Button>

            <Dialog
              open={skipDialogOpen}
              onClose={() =>
                updatingSkipReplied ? null : setSkipDialogOpen(false)
              }
            >
              <DialogTitle>Conversation retention</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  When enabled, any thread that your Page has replied to will be
                  removed from monitoring and its messages will be deleted.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  When disabled, all conversations remain available for review,
                  including those you have replied to.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSkipDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => {
                    handleToggleSkipReplied({
                      target: { checked: !skipReplied },
                    })
                    setSkipDialogOpen(false)
                  }}
                >
                  {skipReplied ? 'Disable' : 'Enable'}
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

export default UserProfile
